import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, CalendarIcon, BuildingIcon } from 'lucide-react';
import { Catalog, Entity, Vendor } from '../CatalogsManagement';
import { flattenOrgUnits, sampleOrgUnits, type OrgUnit } from '../../financial/sharedFinancialData';
interface CatalogFormProps {
  catalog: Catalog | null;
  vendors: Vendor[];
  entities: Entity[];
  onSave: (catalogData: Partial<Catalog>) => void;
  onCancel: () => void;
  locale: string;
}
export function CatalogForm({
  catalog,
  vendors,
  entities,
  onSave,
  onCancel,
  locale
}: CatalogFormProps) {
  // Form state
  const [formData, setFormData] = useState<Partial<Catalog>>({
    name: '',
    description: '',
    vendorId: '',
    activeFrom: new Date().toISOString().split('T')[0],
    activeTo: null,
    isActive: true,
    orgUnitIds: []
  });
  // Selected OrgUnits for UI
  const [selectedOrgUnits, setSelectedOrgUnits] = useState<OrgUnit[]>([]);
  // Get all OrgUnits from shared data
  const allOrgUnits = flattenOrgUnits(sampleOrgUnits);
  // Initialize form with catalog data if editing
  useEffect(() => {
    if (catalog) {
      setFormData({
        name: catalog.name,
        description: catalog.description,
        vendorId: catalog.vendorId,
        activeFrom: catalog.activeFrom,
        activeTo: catalog.activeTo,
        isActive: catalog.isActive,
        orgUnitIds: catalog.orgUnitIds || []
      });
      // Find selected OrgUnits
      const selectedUnits = allOrgUnits.filter(ou => catalog.orgUnitIds?.includes(ou.id));
      setSelectedOrgUnits(selectedUnits);
    }
  }, [catalog, allOrgUnits]);
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    // Handle checkbox
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle OrgUnit selection
  const handleOrgUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = e.target.options;
    const selectedValues: string[] = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedValues.push(options[i].value);
      }
    }
    // Find the OrgUnit objects
    const selectedUnits = allOrgUnits.filter(ou => selectedValues.includes(ou.id));
    setSelectedOrgUnits(selectedUnits);
    // Update form data
    setFormData(prev => ({
      ...prev,
      orgUnitIds: selectedValues
    }));
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-12082">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-12083">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12084">
          {catalog ? 'Edit Catalog' : 'Create Catalog'}
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12085">
          <XIcon className="h-5 w-5" data-id="element-12086" />
        </button>
      </div>
      <form onSubmit={handleSubmit} data-id="element-12087">
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-12088">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-12089">
            {/* Catalog Name */}
            <div className="sm:col-span-4" data-id="element-12090">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-12091">
                Catalog Name
              </label>
              <div className="mt-1" data-id="element-12092">
                <input type="text" name="name" id="name" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.name} onChange={handleChange} required data-id="element-12093" />
              </div>
            </div>

            {/* Vendor */}
            <div className="sm:col-span-3" data-id="element-12094">
              <label htmlFor="vendorId" className="block text-sm font-medium text-gray-700" data-id="element-12095">
                Vendor
              </label>
              <div className="mt-1" data-id="element-12096">
                <select id="vendorId" name="vendorId" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.vendorId} onChange={handleChange} required data-id="element-12097">
                  <option value="" data-id="element-12098">Select Vendor</option>
                  {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-12099">
                      {vendor.name}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Active Status */}
            <div className="sm:col-span-3 flex items-center" data-id="element-12100">
              <div className="flex h-5 items-center" data-id="element-12101">
                <input id="isActive" name="isActive" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={formData.isActive} onChange={handleChange} data-id="element-12102" />
              </div>
              <div className="ml-3 text-sm" data-id="element-12103">
                <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-12104">
                  Active
                </label>
                <p className="text-gray-500" data-id="element-12105">
                  Make this catalog available for use
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="sm:col-span-6" data-id="element-12106">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-12107">
                Description
              </label>
              <div className="mt-1" data-id="element-12108">
                <textarea id="description" name="description" rows={3} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.description} onChange={handleChange} data-id="element-12109" />
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-12110">
                Brief description of the catalog and its contents
              </p>
            </div>

            {/* Active From */}
            <div className="sm:col-span-3" data-id="element-12111">
              <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-12112">
                Active From
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12113">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12114">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-12115" />
                </div>
                <input type="date" name="activeFrom" id="activeFrom" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeFrom} onChange={handleChange} required data-id="element-12116" />
              </div>
            </div>

            {/* Active To */}
            <div className="sm:col-span-3" data-id="element-12117">
              <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-12118">
                Active To
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12119">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12120">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-12121" />
                </div>
                <input type="date" name="activeTo" id="activeTo" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeTo || ''} onChange={handleChange} data-id="element-12122" />
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-12123">
                Leave blank for no end date
              </p>
            </div>

            {/* Organisation Units */}
            <div className="sm:col-span-6" data-id="element-12124">
              <label htmlFor="orgUnits" className="block text-sm font-medium text-gray-700 flex items-center" data-id="element-12125">
                <BuildingIcon className="h-4 w-4 mr-1" data-id="element-12126" />
                Organisation Units
              </label>
              <div className="mt-1" data-id="element-12127">
                <select id="orgUnits" name="orgUnits" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" multiple size={6} value={formData.orgUnitIds || []} onChange={handleOrgUnitChange} data-id="element-12128">
                  {allOrgUnits.map(orgUnit => <option key={orgUnit.id} value={orgUnit.id} data-id="element-12129">
                      {orgUnit.name} ({orgUnit.path})
                      {orgUnit.orgUnitType && ` - ${orgUnit.orgUnitType.name}`}
                    </option>)}
                </select>
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-12130">
                Select which organisation units can access this catalog. Hold
                Ctrl/Cmd to select multiple. Leave empty to make available to
                all.
              </p>
              {selectedOrgUnits.length > 0 && <div className="mt-2 flex flex-wrap gap-2" data-id="element-12131">
                  {selectedOrgUnits.map(orgUnit => <span key={orgUnit.id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-12132">
                      {orgUnit.name}
                    </span>)}
                </div>}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-12133">
          <button type="button" className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={onCancel} data-id="element-12134">
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12135">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-12136" />
            {catalog ? 'Update Catalog' : 'Create Catalog'}
          </button>
        </div>
      </form>
    </div>;
}